import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Descriptions, Input, List, message, Modal, Space, Tag } from "antd";
import firebase from "../../../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import { FIRESTORE_AUDIENCES_TABLE } from "../../../../../constants/FirestoreConstant";
import { useTranslation } from "react-i18next";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useFirestoreConnect } from "react-redux-firebase";

const db = firebase.firestore();

const AudienceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  useFirestoreConnect([{ collection: FIRESTORE_AUDIENCES_TABLE }]);
  const audiences = useSelector((state) => state.firestoreReducer.ordered.audiences);
  const users = useSelector((state) => state.firestoreReducer.ordered.webshopUsers) || [];
  const [search, setSearch] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);

  const audience = useMemo(() => {
    return audiences?.find((a) => a.id === id);
  }, [audiences, id]);
  const members = useMemo(() => (Array.isArray(audience?.members) ? audience.members : []), [audience]);

  const addUsersToAudience = async (selectedUsers) => {
    try {
      const ref = db.collection(FIRESTORE_AUDIENCES_TABLE).doc(id);
      await db.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists) throw new Error("Audience bestaat niet");
        const data = snap.data() || {};
        const current = Array.isArray(data.members) ? data.members : [];
        const toAdd = [];
        selectedUsers.forEach((u) => {
          const exists = current.some((m) => m.userId === u.id);
          if (!exists) {
            toAdd.push({
              userId: u.id,
              email: u.email,
              firstName: u.firstName,
              lastName: u.lastName,
              addedAt: dayjs().unix(),
              addedBy: firebase.auth().currentUser?.uid || "unknown",
            });
          }
        });
        const updated = [...current, ...toAdd];
        tx.update(ref, { members: updated, membersCount: updated.length, updatedAt: dayjs().unix() });
      });
      message.success(t("add_members_success"));
    } catch (e) {
      console.error(e);
      message.error(t("add_members_failed"));
    }
  };

  const removeMember = async (userId) => {
    try {
      const ref = db.collection(FIRESTORE_AUDIENCES_TABLE).doc(id);
      await db.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists) throw new Error("Audience bestaat niet");
        const data = snap.data() || {};
        const current = Array.isArray(data.members) ? data.members : [];
        const updated = current.filter((m) => m.userId !== userId);
        tx.update(ref, { members: updated, membersCount: updated.length, updatedAt: dayjs().unix() });
      });
      message.success(t("remove_member_success"));
    } catch (e) {
      console.error(e);
      message.error(t("remove_member_failed"));
    }
  };

  const openAddModal = () => {
    setIsAddOpen(true);
  };

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((u) => {
      const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      return u.email?.toLowerCase().includes(q) || name.includes(q);
    });
  }, [users, search]);

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 8 }}>
        <Button onClick={() => navigate(-1)} type={"link"}>
          <ArrowLeftOutlined />
          {t("back")}
        </Button>
      </Space>

      <h1>{audience?.name || t("audience")}</h1>

      <Card loading={!audience} style={{ marginBottom: 12 }}>
        {audience && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label={t("type")}>
              {audience.type === "system" ? <Tag color="geekblue">{t("system")}</Tag> : <Tag>{t("custom")}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t("status")}>
              {audience.status === "active" ? <Tag color="green">{t("active")}</Tag> : <Tag>{t("archived")}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t("description")}>{audience.description || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("triggers")}>
              {Array.isArray(audience.triggers) && audience.triggers.length > 0
                ? audience.triggers.map((t) => <Tag key={t}>{t}</Tag>)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("members")}>{Array.isArray(members) ? members.length : 0}</Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{t("members")}</span>
            <Button type="primary" onClick={openAddModal}>
              {t("add_member")}
            </Button>
          </div>
        }
      >
        <List
          dataSource={members}
          locale={{ emptyText: t("no_members") }}
          renderItem={(m) => (
            <List.Item
              actions={[
                <Button size="small" danger onClick={() => removeMember(m.userId)} key="remove">
                  {t("remove")}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={m.firstName + " " + m.lastName || m.email || m.userId}
                description={
                  <span>
                    {m.email}
                    {m.addedAt ? ` • ${dayjs.unix(m.addedAt).format("DD-MM-YYYY HH:mm")}` : ""}
                  </span>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title={t("add_member")}
        open={isAddOpen}
        onCancel={() => setIsAddOpen(false)}
        onOk={() => {
          // Add all filtered users that are selected; for simplicity, we allow single-click add buttons per user
          setIsAddOpen(false);
        }}
        footer={null}
        destroyOnClose
        width={720}
      >
        <Input.Search
          placeholder={t("search_user_placeholder")}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <List
          dataSource={filteredUsers}
          renderItem={(u) => {
            const already = members.some((m) => m.userId === u.id);
            return (
              <List.Item
                actions={[
                  <Button
                    key="add"
                    type="primary"
                    size="small"
                    disabled={already}
                    onClick={() => addUsersToAudience([u])}
                  >
                    {already ? t("already_added") : t("add")}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || u.id}
                  description={u.email}
                />
              </List.Item>
            );
          }}
        />
      </Modal>
    </div>
  );
};

export default AudienceDetail;
